import React from 'react';
import { useUIStore } from '../../store';
import { Globe } from 'lucide-react';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'es', name: 'Español' },
];

interface AuthShellProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

// Shared frame for every signed-out screen (login, register, forgot/reset
// password, verify email, accept invite): brand background, language picker,
// CEBEN logo + title, then the page's own form/card as children. The
// `auth-scope` class re-tints form focus rings to the brand turquoise
// (see index.css) without touching the app-wide .input-field.
const AuthShell: React.FC<AuthShellProps> = ({ title, subtitle, children }) => {
  const { language, setLanguage } = useUIStore();

  return (
    <div className="auth-scope relative min-h-screen flex items-center justify-center overflow-hidden bg-brand-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      {/* Soft turquoise glow, same look as the landing page hero */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 h-[28rem] w-[60rem] -translate-x-1/2 rounded-full bg-brand-400 opacity-25 blur-3xl dark:opacity-10" />
        <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-brand-500 opacity-15 blur-3xl dark:opacity-10" />
        <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-brand-400 opacity-20 blur-3xl dark:opacity-10" />
      </div>

      <div className="absolute top-4 right-4 flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
        <Globe className="h-4 w-4" />
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value as 'en' | 'tr' | 'es')}
          className="text-sm border rounded-lg px-2 py-1 border-brand-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-400"
        >
          {languages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.name}
            </option>
          ))}
        </select>
      </div>

      <div className="relative max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl dark:bg-white dark:p-1.5">
              <img src="/logo.png" alt="CEBEN" className="h-full w-full object-contain" />
            </span>
            <span className="text-2xl font-extrabold tracking-tight text-brand-ink dark:text-gray-100">CEBEN</span>
          </div>
          <h2 className="mt-8 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100">{title}</h2>
          {subtitle && <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  );
};

export default AuthShell;
